import React, { useEffect } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import OnboardingHeader from "./OnboardingHeader";
import StepProgressBar from "./StepProgressBar";
import OnboardingCard from "./OnboardingCard";
import OnboardingInput from "./OnboardingInput";
import OnboardingButton from "./OnboardingButton";
import OnboardingSelect from "./OnboardingSelect";
import { useAppDispatch, useAppSelector } from "../../store/store";
import { updatePurlUser } from "../../store/reducers/purlUserReducer";
import { updateStage } from "../../store/reducers/stageReducer";
import { useUpdateIncomeLocationMutation, useGetStatesQuery } from "../../services/api";
import { showNotification } from "../../services/showNotification";
import { incomeLocationSchema } from "../../yup";
import useHasPermission from "../../hooks/useHasPermission";
import { ReactComponent as DollarIcon } from "../../assets/svgs/dollar.svg";

interface IncomeLocationForm {
  annualIncome: string;
  stateOfResidence: string;
}

const STEP_KEY = "incomeLocationCompleted" as const;

const IncomeLocation = () => {
  const user = useAppSelector((state) => state.purlUser);
  const dispatch = useAppDispatch();
  const { hasPermission } = useHasPermission();
  const [updateIncomeLocation, { isLoading: isUpdating }] = useUpdateIncomeLocationMutation();
  const { data: statesData } = useGetStatesQuery(undefined);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<IncomeLocationForm>({
    resolver: yupResolver(incomeLocationSchema),
    defaultValues: { annualIncome: "", stateOfResidence: "" },
  });

  useEffect(() => {
    reset({
      annualIncome: user.annualIncome ?? "",
      stateOfResidence: user.stateOfResidence ?? "",
    });
  }, [user.annualIncome, user.stateOfResidence, reset]);

  const onSubmit: SubmitHandler<IncomeLocationForm> = async (data) => {
    
    try {
      const res = await updateIncomeLocation({ 
        annualIncome: data.annualIncome, 
        stateOfResidence: data.stateOfResidence 
      }).unwrap();
      
      dispatch(updatePurlUser({ 
        annualIncome: res.data.annualIncome, 
        stateOfResidence: res.data.stateOfResidence 
      }));
      dispatch(updateStage({ [STEP_KEY]: true }));
    } catch {
      showNotification("error", "Something went wrong. Please try again.");
    }
  };

  const handleBack = () => {
    dispatch(updateStage({ profileVerificationCompleted: false }));
  };

  const stateOptions = statesData?.data?.map((state: { _id: string; state: string }) => ({
    label: state.state,
    value: state.state, 
  })) || [];

  return (
    <div className="min-h-screen bg-[#F8F9FA] relative">
      <div className="fixed inset-0 bg-[#F8F9FA] -z-10 pointer-events-none" />
      <OnboardingHeader />
      <main className="w-full max-w-5xl mx-auto p-10 text-left">
        <StepProgressBar stepKey={STEP_KEY} />
        
        <OnboardingCard className="p-8 sm:p-10 mt-8">
          <form onSubmit={handleSubmit(onSubmit)} noValidate className="w-full">
            <div className="mb-8 text-left">
              <h2 className="text-3xl font-bold text-[#030724] text-left font-['Canela_Text']">
                Income & Location
              </h2>
              <p className="text-base font-normal text-[#46464D] mt-2 font-['Proxima_Nova']">
                To provide the most accurate financial forecasting and tax strategies, we need a high-level view of your current economic standing and residency.
              </p>
            </div>

            <div className="mb-4">
              <OnboardingInput 
                label="Annual Gross Income" 
                placeholder="0.00" 
                icon={<DollarIcon />}
                error={errors.annualIncome?.message}
                disabled={!hasPermission("editAnnualIncome")}
                {...register("annualIncome")}
              />

              <p className="font-['Inter',_sans-serif] font-normal italic text-sm leading-normal text-[#6F8AB2] mt-2">
                Please include all sources of income before deductions.
              </p>
            </div>

            <div className="mb-8">
               <OnboardingSelect 
                 label="State of Residence" 
                 options={stateOptions} 
                 value={watch("stateOfResidence")} 
                 onChange={(val) => setValue("stateOfResidence", val, { shouldValidate: true })}
                 disabled={!hasPermission("editStateOfResidence")}
                 error={errors.stateOfResidence?.message as string}
               />
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
               {/* {hasPermission("profileVerificationForm") && (
                 <div className="flex-[2]">
                    <OnboardingButton 
                      title="BACK" 
                      variant="outline" 
                      type="button"
                      onClick={handleBack} 
                      disabled={isUpdating}
                    />
                 </div>
               )} */}
               <div className="flex-[3]">
                  <OnboardingButton 
                    title="CONTINUE" 
                    variant="filled" 
                    type="submit" 
                    loading={isUpdating}
                    disabled={isUpdating}
                  />
               </div>
            </div>
          </form>
        </OnboardingCard>
      </main>
    </div>
  );
};

export default IncomeLocation;